import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { nextDrawText } from '../bingo/ui.js';
import type { BingoPublicState } from '../../shared/bingo.js';

/** The announcement disappears this long after the announced time (as on the server). */
const VISIBLE_AFTER_MS = 3 * 60 * 60 * 1000;

/**
 * Bingo in the lobby (user request 2026-09-30): the next announced draw with a button to the coupon
 * page, or "Bingo is on now" while a game runs. Nothing is shown without either.
 */
export function BingoLobbyCard() {
  const [state, setState] = useState<BingoPublicState | null>(null);
  const version = useRef<number | null>(null);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const since = version.current === null ? '' : `?since=${version.current}`;
        const r = await fetch(`/api/bingo/state${since}`, { cache: 'no-store' });
        const data = (await r.json()) as BingoPublicState & { unchanged?: boolean };
        if (!data.unchanged) {
          version.current = data.version;
          setState(data);
        }
      } catch {
        // Next round.
      }
      if (!stopped) timer = setTimeout(poll, 30_000);
    };
    void poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, []);

  const running = state?.game?.status === 'running';
  const next = state?.next && state.next.at + VISIBLE_AFTER_MS > Date.now() ? state.next : null;
  if (!running && !next) return null;
  const b = t.bingoLobby;
  return (
    <section class={`card bingo-lobby ${running ? 'live' : ''}`} aria-live="polite">
      <span class="bingo-lobby-icon" aria-hidden="true">
        🎱
      </span>
      <div class="bingo-lobby-text">
        {running ? (
          <>
            <strong>{b.live}</strong>
            <span>{b.liveHint}</span>
          </>
        ) : (
          <>
            <strong>{b.next(nextDrawText(next!.at))}</strong>
            {next!.text && <span>{next!.text}</span>}
            <span class="muted">{b.hint}</span>
          </>
        )}
      </div>
      <a class="btn btn-primary" href="/bingo/" target="_blank" rel="noopener">
        {running ? b.follow : b.get}
      </a>
    </section>
  );
}
